"use client";

import { useState } from "react";
import { useTranslation } from "react-i18next";

/**
 * 2026-04-30 — clears the per-browser UI preference keys so a user
 * who's accumulated stale state can start fresh.
 *
 * Currently scoped to:
 *   - granbay.orderbookCollapsed (PR #476)
 *   - granbay.recentTradesCollapsed (PR #480)
 *   - granbay.recentSearches (PR #494)
 *
 * NOT cleared (intentional):
 *   - granbay.theme — handled by the ThemeToggle on this page
 *   - granbay.region — region detector reseeds on next request
 *   - auth tokens — separate concern (sign out)
 *
 * New keys should be added to UI_PREF_KEYS as they ship.
 */
const UI_PREF_KEYS = [
  "granbay.orderbookCollapsed",
  "granbay.recentTradesCollapsed",
  "granbay.recentSearches",
] as const;

export function ResetLocalUiPrefs() {
  const { t } = useTranslation();
  const [done, setDone] = useState(false);

  function handleReset() {
    try {
      for (const key of UI_PREF_KEYS) {
        localStorage.removeItem(key);
      }
      setDone(true);
      // Auto-clear the confirmation after a moment so the button
      // becomes actionable again if the user expanded a section
      // and wants to reset once more.
      setTimeout(() => setDone(false), 2500);
    } catch {
      // Storage blocked — nothing to reset; render the same
      // confirmation state since the effective outcome is the same
      // (no persisted prefs to honour).
      setDone(true);
      setTimeout(() => setDone(false), 2500);
    }
  }

  return (
    <div className="flex items-center justify-between">
      <div>
        <p className="text-[13px] font-semibold">
          {t("resetLocalUiPrefs.title")}
        </p>
        <p className="mt-0.5 text-[11px] text-[var(--color-text3)]">
          {t("resetLocalUiPrefs.body")}
        </p>
      </div>
      <button
        type="button"
        onClick={handleReset}
        className="rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] px-3 py-2 text-[12px] font-bold text-[var(--color-text2)] transition-colors hover:border-[var(--color-brand)] hover:text-[var(--color-brand)]"
      >
        {done ? t("resetLocalUiPrefs.cleared") : t("resetLocalUiPrefs.reset")}
      </button>
    </div>
  );
}
